// src/utils/assetCsvFields.ts

import dayjs from 'dayjs';

export type CsvField = {
  key: string;
  label: string;
  extractor: (asset: any) => string;
  enabled: boolean;
};

const renderProcessingStatus = (step: string | undefined | null) => {
  if (!step) return "Not Complete";

  if (step === "COMPLETE") return "COMPLETE";

  const match = step.match(/asset(?:Processing|Syndication)_(.+?)_serviceFlag/);
  if (match && match[1]) {
    return match[1].toUpperCase();
  }

  return "Not Complete";
};

export const assetCsvFields: CsvField[] = [
  // ✅ Enabled fields (original order preserved)
  {
    key: 'assetId',
    label: 'Asset ID',
    extractor: asset => asset.assetId || '',
    enabled: true
  },
  {
    key: 'postDate',
    label: 'Post Date',
    extractor: asset => dayjs(Number(asset.sk) * 1000).format('YYYY-MM-DD') || '',
    enabled: true
  },
  {
    key: 'bwImageCount',
    label: 'BW Image Count',
    extractor: asset => String(asset.imageCounts?.exteriorRaw || 0),
    enabled: true
  },
  {
    key: 'croppedImageCount',
    label: 'Cropped Image Count',
    extractor: asset => String(asset.imageCounts?.exteriorCrop || 0),
    enabled: true
  },
  {
    key: 'croppedProcessStatus',
    label: 'Cropped Process Status',
    extractor: asset => asset.assetProcessing_crop_processFlag_status || '',
    enabled: true
  },
  {
    key: 'featuredImageCount',
    label: 'Featured Image Count',
    extractor: asset => String(asset.imageCounts?.exteriorFeatured || 0),
    enabled: true
  },
  {
    key: 'featuredProcessStatus',
    label: 'Featured Process Status',
    extractor: asset => asset.assetProcessing_featured_processFlag_status || '',
    enabled: true
  },
  {
    key: 'panoImageCount',
    label: 'Pano Image Count',
    extractor: asset => String(asset.imageCounts?.panoRaw || 0),
    enabled: true
  },
  {
    key: 'syndicationStatus',
    label: 'Syndication Status',
    extractor: asset => renderProcessingStatus(asset.flowControl_assetSyndication_currentStep) || '',
    enabled: true
  },
  {
    key: 'processingStatus',
    label: 'Processing Status',
    extractor: asset => renderProcessingStatus(asset.flowControl_assetProcessing_currentStep) || '',
    enabled: true
  },

  // 🔠 Disabled fields (alphabetized by label)
  {
    key: 'ratio',
    label: 'Aspect Ratio',
    extractor: asset => asset.ratio || '',
    enabled: false
  },
  {
    key: 'backgroundReplacedImageCount',
    label: 'Background Replaced Image Count',
    extractor: asset => String(asset.imageCounts?.exteriorBgr || 0),
    enabled: false
  },
  {
    key: 'backgroundReplacedProcessStatus',
    label: 'Background Replaced Process Status',
    extractor: asset => asset.assetProcessing_bgrExterior_processFlag_status || '',
    enabled: false
  },
  {
    key: 'cameraDirection',
    label: 'Camera Direction',
    extractor: asset => asset.cameraDirection || '',
    enabled: false
  },
  {
    key: 'systemCode',
    label: 'System Code',
    extractor: asset => asset.systemCode || '',
    enabled: false
  },
  {
    key: 'configurationName',
    label: 'Configuration Name',
    extractor: asset => asset.configurationName || '',
    enabled: false
  },
  {
    key: 'manualImageCount',
    label: 'Manual Image Count',
    extractor: asset => String(asset.imageCounts?.manualRaw || 0),
    enabled: false
  },
  {
    key: 'maskImageCount',
    label: 'Mask Image Count',
    extractor: asset => String(asset.imageCounts?.exteriorMask || 0),
    enabled: false
  },
  {
    key: 'maskProcessStatus',
    label: 'Masking Process Status',
    extractor: asset => asset.assetProcessing_masking_processFlag_status || '',
    enabled: false
  },
  {
    key: 'undercarriageRawCount',
    label: 'Undercarriage Image Count',
    extractor: asset => String(asset.imageCounts?.undercarriageRaw || 0),
    enabled: false
  }
];
